import { Link } from "react-router-dom";

import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { usePageTitle } from "@/components/PageTitleContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { listAccessibleCategories } from "@/features/help/docsContent";

/**
 * Two-column docs chrome: topics index (left) + detailed content (right).
 * headerMode "full" shows title/description; "article" leaves the heading to the article body.
 */
export function DocsLayout({
  title = "Docs",
  description,
  actions,
  activeSlug,
  headerMode = "full",
  children,
}) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const categories = listAccessibleCategories(user, can);

  usePageTitle(typeof title === "string" ? title : undefined);

  return (
    <PageShell>
      <PageHeader
        title={headerMode === "full" ? title : undefined}
        description={headerMode === "full" ? description : undefined}
        actions={actions}
      />
      <PageBody>
        <div className="grid gap-6 md:grid-cols-[minmax(15rem,18rem)_minmax(0,1fr)] md:gap-0 md:divide-x md:rounded-lg md:border">
          <aside className="md:bg-muted/20 md:p-4 lg:p-5">
            <nav aria-label="Topics index" className="space-y-4 text-sm md:sticky md:top-20">
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Topics
                </p>
                <Link
                  to="/docs"
                  className={cn(
                    "block rounded-md px-2 py-1.5 font-medium transition-colors hover:bg-muted",
                    !activeSlug
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  Overview
                </Link>
              </div>
              {categories.map((category) => (
                <div key={category.id} className="space-y-1">
                  <p className="px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {category.title}
                  </p>
                  <ul className="space-y-0.5">
                    {category.guides.map((guide) => (
                      <li key={guide.slug}>
                        <Link
                          to={`/docs/${guide.slug}`}
                          className={cn(
                            "block rounded-md px-2 py-1.5 leading-snug transition-colors hover:bg-muted hover:text-foreground",
                            activeSlug === guide.slug
                              ? "bg-muted font-medium text-foreground"
                              : "text-muted-foreground",
                          )}
                        >
                          {guide.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>

          <div className="min-w-0 space-y-8 md:p-6 lg:p-8">{children}</div>
        </div>
      </PageBody>
    </PageShell>
  );
}

export function DocsSeeAlso({ guides }) {
  if (!guides?.length) return null;

  return (
    <section className="space-y-3 border-t pt-6">
      <h2 className="text-base font-semibold tracking-tight">See also</h2>
      <ul className="space-y-2 text-sm">
        {guides.map((guide) => (
          <li key={guide.slug}>
            <Link
              to={`/docs/${guide.slug}`}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              {guide.title}
            </Link>
            <span className="text-muted-foreground"> — {guide.summary}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function DocsOpenFeature({ guide }) {
  if (!guide?.to) return null;
  return (
    <Button asChild variant="outline" size="sm">
      <Link to={guide.to}>Open in app</Link>
    </Button>
  );
}
